import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SearchableSelect } from "./searchable-select";
import { FormDialog, Field } from "./form-dialog";
import { nameField, lookupValue, optionalIsoDateNotFuture } from "@/lib/validators";

const schema = z.object({
  title: nameField(120),
  client: lookupValue("Pick a client"),
  value: z.coerce.number().min(100, "Min $100").max(10_000_000, "Max $10,000,000"),
  sent: optionalIsoDateNotFuture,
  status: z.enum(["draft", "sent", "signed", "expired"]),
});
type Values = z.infer<typeof schema>;

export function NewContractDialog() {
  const { register, handleSubmit, formState: { errors, isDirty }, setValue, watch, reset } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { title: "", client: "", value: 0, sent: "", status: "draft" },
  });
  const [clientLabel, setClientLabel] = useState("");
  return (
    <FormDialog
      trigger={<Button size="sm" className="gap-1.5"><Plus className="size-3.5" /> New contract</Button>}
      title="Draft a contract"
      description="Create a contract record. You can attach a PDF and send for e-sign later."
      submitLabel="Create contract"
      isDirty={isDirty}
      onReset={() => { reset(); setClientLabel(""); }}
      onSubmit={() => new Promise<void>((resolve, reject) => {
        handleSubmit((v) => {
          toast.success("Contract drafted", { description: v.title });
          resolve();
        }, () => reject())();
      })}
    >
      <Field label="Title" htmlFor="ct-title" error={errors.title?.message}>
        <Input id="ct-title" placeholder="Arc'teryx SS25 Campaign" {...register("title")} />
      </Field>
      <Field label="Client" error={errors.client?.message}>
        <SearchableSelect
          resource="clients"
          value={watch("client")}
          selectedLabel={clientLabel}
          onChange={(v, label) => {
            setValue("client", v, { shouldValidate: true, shouldDirty: true });
            setClientLabel(label);
          }}
          placeholder="Select client"
          searchPlaceholder="Search clients…"
        />
      </Field>
      <div className="grid grid-cols-3 gap-3">
        <Field label="Value (USD)" htmlFor="ct-value" error={errors.value?.message}>
          <Input id="ct-value" type="number" min={0} step={100} {...register("value")} />
        </Field>
        <Field label="Sent date" htmlFor="ct-sent" error={errors.sent?.message}>
          <Input id="ct-sent" type="date" {...register("sent")} />
        </Field>
        <Field label="Status" error={errors.status?.message}>
          <Select value={watch("status")} onValueChange={(v) => setValue("status", v as Values["status"])}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="signed">Signed</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      </div>
    </FormDialog>
  );
}
